import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { AdminPageHeader, AdminPageShell, NoticePanel, StatCard } from './AdminPageUI';

const AdminProgressManagement = () => {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [studentProgress, setStudentProgress] = useState([]);
  const [studentAchievements, setStudentAchievements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('progress');

  // New progress form
  const [newProgress, setNewProgress] = useState({
    subject: '',
    score: '',
    completed: false,
    notes: ''
  });

  // New achievement form
  const [newAchievement, setNewAchievement] = useState({
    title: '',
    description: '',
    type: 'academic'
  });

  useEffect(() => {
    loadStudents();
  }, []);

  useEffect(() => {
    if (selectedStudent) {
      loadStudentData();
    }
  }, [selectedStudent]);

  const loadStudents = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.STUDENTS);
      const studentsData = await response.json();
      setStudents(studentsData);
    } catch (error) {
      console.error('Error loading students:', error);
    }
  };

  const loadStudentData = async () => {
    if (!selectedStudent) return;

    setLoading(true);
    try {
      const [progressRes, achievementsRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.PROGRESS}/${selectedStudent}`),
        fetch(`${API_ENDPOINTS.ACHIEVEMENTS}/${selectedStudent}`)
      ]);

      const progressData = await progressRes.json();
      const achievementsData = await achievementsRes.json();

      setStudentProgress(progressData);
      setStudentAchievements(achievementsData);
    } catch (error) {
      console.error('Error loading student data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addProgress = async (e) => {
    e.preventDefault();
    if (!selectedStudent || !newProgress.subject) {
      alert('Please select a student and enter subject');
      return;
    }

    try {
      const progressData = {
        username: selectedStudent,
        subject: newProgress.subject,
        score: parseFloat(newProgress.score) || 0,
        completed: newProgress.completed,
        notes: newProgress.notes
      };

      const response = await fetch(API_ENDPOINTS.PROGRESS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(progressData)
      });

      if (response.ok) {
        setNewProgress({ subject: '', score: '', completed: false, notes: '' });
        loadStudentData(); // Refresh data
        alert('Progress added successfully!');
      } else {
        alert('Failed to add progress');
      }
    } catch (error) {
      console.error('Error adding progress:', error);
      alert('Failed to add progress');
    }
  };

  const updateProgress = async (progressId, updates) => {
    try {
      const existingProgress = studentProgress.find(p => p.id === progressId);
      if (!existingProgress) return;

      const updatedProgress = { ...existingProgress, ...updates };

      const response = await fetch(API_ENDPOINTS.PROGRESS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedProgress)
      });

      if (response.ok) {
        loadStudentData();
      }
    } catch (error) {
      console.error('Error updating progress:', error);
    }
  };

  const deleteProgress = async (progressId) => {
    if (!window.confirm('Are you sure you want to delete this progress record?')) return;

    try {
      // Find the progress record to delete
      const progressToDelete = studentProgress.find(p => p.id === progressId);
      if (!progressToDelete) return;

      // Since we don't have a direct delete endpoint, we'll update it with a deleted flag
      // In a real implementation, you'd have a DELETE endpoint
      alert('Delete functionality would be implemented with a proper DELETE API endpoint');
    } catch (error) {
      console.error('Error deleting progress:', error);
    }
  };

  const addAchievement = async (e) => {
    e.preventDefault();
    if (!selectedStudent || !newAchievement.title) {
      alert('Please select a student and enter achievement title');
      return;
    }

    try {
      const achievementData = {
        username: selectedStudent,
        title: newAchievement.title,
        description: newAchievement.description,
        type: newAchievement.type
      };

      const response = await fetch(API_ENDPOINTS.ACHIEVEMENTS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(achievementData)
      });

      if (response.ok) {
        setNewAchievement({ title: '', description: '', type: 'academic' });
        loadStudentData(); // Refresh data
        alert('Achievement added successfully!');
      } else {
        alert('Failed to add achievement');
      }
    } catch (error) {
      console.error('Error adding achievement:', error);
      alert('Failed to add achievement');
    }
  };

  const getOverallStats = () => {
    const totalSubjects = studentProgress.length;
    const completedSubjects = studentProgress.filter(p => p.completed).length;
    const averageScore = totalSubjects > 0
      ? studentProgress.reduce((sum, p) => sum + (p.score || 0), 0) / totalSubjects
      : 0;

    return {
      totalSubjects,
      completedSubjects,
      averageScore: Math.round(averageScore * 100) / 100,
      completionRate: totalSubjects > 0 ? Math.round((completedSubjects / totalSubjects) * 100) : 0
    };
  };

  const getSubjectProgress = () => {
    const subjectMap = {};

    studentProgress.forEach(progress => {
      if (!subjectMap[progress.subject]) {
        subjectMap[progress.subject] = {
          subject: progress.subject,
          scores: [],
          completedCount: 0,
          totalCount: 0
        };
      }

      subjectMap[progress.subject].scores.push(progress.score || 0);
      subjectMap[progress.subject].totalCount++;

      if (progress.completed) {
        subjectMap[progress.subject].completedCount++;
      }
    });

    return Object.values(subjectMap).map(subject => ({
      ...subject,
      averageScore: subject.scores.length > 0
        ? Math.round((subject.scores.reduce((a, b) => a + b, 0) / subject.scores.length) * 100) / 100
        : 0,
      completionRate: subject.totalCount > 0
        ? Math.round((subject.completedCount / subject.totalCount) * 100)
        : 0
    }));
  };

  const exportProgressReport = () => {
    if (studentProgress.length === 0) {
      alert('No progress data to export');
      return;
    }

    const csvData = [
      ['Subject', 'Score', 'Completed', 'Last Updated', 'Notes'],
      ...studentProgress.map(progress => [
        progress.subject,
        progress.score || 'N/A',
        progress.completed ? 'Yes' : 'No',
        new Date(progress.lastUpdated || progress.date).toLocaleDateString(),
        progress.notes || ''
      ])
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `progress-report-${selectedStudent}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const selectedStudentData = students.find(s => s.username === selectedStudent);
  const overallStats = getOverallStats();
  const subjectProgress = getSubjectProgress();

  return (
    <AdminPageShell>
        <AdminPageHeader
          eyebrow="Academic Performance"
          title="Progress Management"
          description="Update grades, review completion progress, and record student achievements from one focused workspace."
          actions={(
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700"
          >
            Back to Dashboard
          </button>
          )}
        />

        {/* Student Selection */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Select Student</h2>
          <select
            value={selectedStudent}
            onChange={(e) => setSelectedStudent(e.target.value)}
            className="border rounded px-4 py-2 w-full max-w-md"
          >
            <option value="">Choose a student...</option>
            {students.map((student) => (
              <option key={student.username} value={student.username}>
                {student.firstName} {student.lastName} ({student.className})
              </option>
            ))}
          </select>

          {selectedStudentData && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold text-blue-800">
                {selectedStudentData.firstName} {selectedStudentData.lastName}
              </h3>
              <p className="text-sm text-blue-600">Class: {selectedStudentData.className}</p>
            </div>
          )}
        </div>

        {selectedStudent && (
          <>
            {overallStats.averageScore < 50 && (
              <NoticePanel title="Performance attention needed" tone="rose">
                This student average is below 50. Review subject records, add notes, and assign follow-up support before the next reporting cycle.
              </NoticePanel>
            )}
            {/* Overview Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <StatCard label="Subject Records" value={overallStats.totalSubjects} tone="sky" detail="Tracked for selected student" />
              <StatCard label="Completed" value={overallStats.completedSubjects} tone="emerald" detail="Marked complete" />
              <StatCard label="Completion Rate" value={`${overallStats.completionRate}%`} tone="violet" detail="Across recorded subjects" />
              <StatCard label="Average Score" value={overallStats.averageScore} tone={overallStats.averageScore < 50 ? 'rose' : 'amber'} detail="Academic performance" />
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow mb-8">
              <div className="border-b">
                <nav className="flex">
                  <button
                    onClick={() => setActiveTab('progress')}
                    className={`px-6 py-3 text-sm font-medium ${
                      activeTab === 'progress'
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Progress Management
                  </button>
                  <button
                    onClick={() => setActiveTab('achievements')}
                    className={`px-6 py-3 text-sm font-medium ${
                      activeTab === 'achievements'
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Achievements & Awards
                  </button>
                  <button
                    onClick={() => setActiveTab('reports')}
                    className={`px-6 py-3 text-sm font-medium ${
                      activeTab === 'reports'
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Reports & Analytics
                  </button>
                </nav>
              </div>

              <div className="p-6">
                {activeTab === 'progress' && (
                  <div>
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-bold text-gray-800">Manage Student Progress</h2>
                      {studentProgress.length > 0 && (
                        <button
                          onClick={exportProgressReport}
                          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                        >
                          Export Report
                        </button>
                      )}
                    </div>

                    {/* Add Progress Form */}
                    <div className="bg-gray-50 rounded-lg p-6 mb-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Add New Progress Record</h3>
                      <form onSubmit={addProgress} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <input
                          type="text"
                          placeholder="Subject (e.g., Mathematics)"
                          value={newProgress.subject}
                          onChange={(e) => setNewProgress({...newProgress, subject: e.target.value})}
                          className="border rounded px-3 py-2"
                          required
                        />
                        <input
                          type="number"
                          placeholder="Score (0-100)"
                          value={newProgress.score}
                          onChange={(e) => setNewProgress({...newProgress, score: e.target.value})}
                          className="border rounded px-3 py-2"
                          min="0"
                          max="100"
                        />
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="completed"
                            checked={newProgress.completed}
                            onChange={(e) => setNewProgress({...newProgress, completed: e.target.checked})}
                            className="mr-2"
                          />
                          <label htmlFor="completed" className="text-sm">Completed</label>
                        </div>
                        <input
                          type="text"
                          placeholder="Notes (optional)"
                          value={newProgress.notes}
                          onChange={(e) => setNewProgress({...newProgress, notes: e.target.value})}
                          className="border rounded px-3 py-2"
                        />
                        <div className="md:col-span-2 lg:col-span-4">
                          <button
                            type="submit"
                            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
                          >
                            Add Progress Record
                          </button>
                        </div>
                      </form>
                    </div>

                    {/* Progress Records */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-800">Progress Records</h3>
                      {loading ? (
                        <div className="text-center py-8">Loading progress data...</div>
                      ) : studentProgress.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          No progress records found. Add the first record above.
                        </div>
                      ) : (
                        studentProgress.map((progress) => (
                          <div key={progress.id} className="border rounded-lg p-4 bg-white">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex-1">
                                <h4 className="font-bold text-gray-800">{progress.subject}</h4>
                                <div className="flex items-center space-x-4 mt-2">
                                  <span className="text-sm text-gray-600">
                                    Score: <span className="font-semibold">{progress.score || 'N/A'}</span>
                                  </span>
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    progress.completed
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {progress.completed ? 'Completed' : 'In Progress'}
                                  </span>
                                  <span className="text-sm text-gray-500">
                                    {new Date(progress.lastUpdated || progress.date).toLocaleDateString()}
                                  </span>
                                </div>
                                {progress.notes && (
                                  <p className="text-sm text-gray-600 mt-2">{progress.notes}</p>
                                )}
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => updateProgress(progress.id, { completed: !progress.completed })}
                                  className={`px-3 py-1 rounded text-xs ${
                                    progress.completed
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-green-100 text-green-800'
                                  }`}
                                >
                                  {progress.completed ? 'Mark Incomplete' : 'Mark Complete'}
                                </button>
                                <button
                                  onClick={() => deleteProgress(progress.id)}
                                  className="px-3 py-1 rounded text-xs bg-red-100 text-red-800"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'achievements' && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-6">Manage Achievements & Awards</h2>

                    {/* Add Achievement Form */}
                    <div className="bg-gray-50 rounded-lg p-6 mb-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Add New Achievement</h3>
                      <form onSubmit={addAchievement} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="Achievement Title"
                          value={newAchievement.title}
                          onChange={(e) => setNewAchievement({...newAchievement, title: e.target.value})}
                          className="border rounded px-3 py-2"
                          required
                        />
                        <select
                          value={newAchievement.type}
                          onChange={(e) => setNewAchievement({...newAchievement, type: e.target.value})}
                          className="border rounded px-3 py-2"
                        >
                          <option value="academic">Academic</option>
                          <option value="sports">Sports</option>
                          <option value="arts">Arts</option>
                          <option value="leadership">Leadership</option>
                          <option value="community">Community Service</option>
                        </select>
                        <textarea
                          placeholder="Achievement Description"
                          value={newAchievement.description}
                          onChange={(e) => setNewAchievement({...newAchievement, description: e.target.value})}
                          className="border rounded px-3 py-2 md:col-span-2"
                          rows="3"
                        />
                        <div className="md:col-span-2">
                          <button
                            type="submit"
                            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
                          >
                            Add Achievement
                          </button>
                        </div>
                      </form>
                    </div>

                    {/* Achievements List */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-800">Student Achievements</h3>
                      {studentAchievements.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          No achievements recorded yet. Add the first achievement above.
                        </div>
                      ) : (
                        studentAchievements.map((achievement) => (
                          <div key={achievement.id} className="border rounded-lg p-4 bg-yellow-50">
                            <div className="flex items-start space-x-4">
                              <div className="text-3xl">Award</div>
                              <div className="flex-1">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <h4 className="font-bold text-gray-800">{achievement.title}</h4>
                                    <p className="text-sm text-gray-600 mb-2">
                                      {new Date(achievement.date).toLocaleDateString()} ?
                                      <span className={`ml-2 px-2 py-1 rounded text-xs ${
                                        achievement.type === 'academic' ? 'bg-blue-100 text-blue-800' :
                                        achievement.type === 'sports' ? 'bg-green-100 text-green-800' :
                                        achievement.type === 'arts' ? 'bg-purple-100 text-purple-800' :
                                        achievement.type === 'leadership' ? 'bg-orange-100 text-orange-800' :
                                        'bg-gray-100 text-gray-800'
                                      }`}>
                                        {achievement.type}
                                      </span>
                                    </p>
                                    {achievement.description && (
                                      <p className="text-gray-700">{achievement.description}</p>
                                    )}
                                  </div>
                                  <button className="text-red-600 hover:text-red-800 text-sm">
                                    Remove
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'reports' && (
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-6">Progress Reports & Analytics</h2>

                    {/* Subject-wise Progress */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Subject-wise Progress</h3>
                        <div className="space-y-3">
                          {subjectProgress.map((subject) => (
                            <div key={subject.subject} className="border rounded p-3">
                              <div className="flex justify-between items-center mb-2">
                                <span className="font-medium text-gray-800">{subject.subject}</span>
                                <span className="text-sm text-gray-600">
                                  {subject.completedCount}/{subject.totalCount} completed
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-blue-600 h-2 rounded-full"
                                  style={{ width: `${subject.completionRate}%` }}
                                ></div>
                              </div>
                              <div className="flex justify-between text-xs text-gray-600 mt-1">
                                <span>{subject.completionRate}% Complete</span>
                                <span>Avg Score: {subject.averageScore}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Performance Insights</h3>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Strongest Subject</span>
                            <span className="font-medium text-green-600">
                              {subjectProgress.reduce((best, current) =>
                                current.averageScore > (best?.averageScore || 0) ? current : best, null
                              )?.subject || 'N/A'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Areas for Improvement</span>
                            <span className="font-medium text-orange-600">
                              {subjectProgress.filter(s => s.completionRate < 70).length} subjects
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Recent Progress</span>
                            <span className="font-medium text-blue-600">
                              {studentProgress.filter(p =>
                                new Date(p.lastUpdated || p.date) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                              ).length} updates this week
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Export Options */}
                    <div className="bg-gray-50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Export Options</h3>
                      <div className="flex space-x-4">
                        <button
                          onClick={exportProgressReport}
                          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                        >
                          Export Progress Report (CSV)
                        </button>
                        <button
                          onClick={() => alert('Full academic report export would be implemented here')}
                          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                        >
                          Generate Academic Report (PDF)
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {!selectedStudent && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="text-6xl mb-4">Profile</div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Select a Student</h3>
            <p className="text-gray-600">Choose a student from the dropdown above to manage their progress and achievements.</p>
          </div>
        )}
    </AdminPageShell>
  );
};

export default AdminProgressManagement;



